import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { m } from 'motion/react';
import { CircleAlert, Info, OctagonAlert, TriangleAlert } from 'lucide-react';

import {
  notifications as api,
  type Notification,
  type NotificationFilters,
  type NotificationList,
  type NotificationSeverity,
} from '../../api/notifications';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useFormat } from '../../i18n/format';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useAuth } from '../../stores/auth';

import { useNotificationsStream } from './useNotificationsStream';

/**
 * El centro de notificaciones de `13 §2`: la bandeja entera, de todas las
 * empresas.
 *
 * La severidad nunca va solo en el color: cada fila lleva un icono *y* la
 * palabra, porque el gerente que no distingue el rojo del ámbar es exactamente
 * aquel para quien era el error (`13 §7`). Marcar como leída es optimista con
 * reversión (`13 §6`): la fila se apaga en cuanto se pulsa, y pulsar una
 * notificación con `action_url` lleva además adonde apunta.
 */

const SEVERITIES: NotificationSeverity[] = ['info', 'warning', 'error', 'critical'];

/**
 * Qué filas enseña la bandeja. Por defecto solo las que quedan por leer: la
 * bandeja es una lista de pendientes, y leer una la saca de ahí. Las leídas
 * siguen a un selector de distancia, y «todas» junta ambas.
 */
type ReadState = 'unread' | 'read' | 'all';

const READ_STATES: ReadState[] = ['unread', 'read', 'all'];

function readFilter(state: ReadState): boolean | undefined {
  return state === 'all' ? undefined : state === 'read';
}

/**
 * Stable fallback for the companies selector. A literal `?? []` inside the
 * selector returns a fresh array on every snapshot read, which
 * `useSyncExternalStore` treats as "the store changed" — forever.
 */
const NO_COMPANIES: { id: string; name: string; slug: string; status: string }[] = [];

function SeverityBadge({ severity }: { severity: NotificationSeverity }) {
  const t = useT();
  const badge = {
    info: { Icon: Info, tone: 'info' as const },
    warning: { Icon: TriangleAlert, tone: 'warning' as const },
    error: { Icon: CircleAlert, tone: 'danger' as const },
    critical: { Icon: OctagonAlert, tone: 'danger' as const },
  }[severity];

  return (
    <Badge tone={badge.tone} size="sm" icon={<badge.Icon />}>
      {t(`severity.${severity}` as Key)}
    </Badge>
  );
}

export function NotificationsScreen() {
  const t = useT();
  const format = useFormat();
  const client = useQueryClient();
  const navigate = useNavigate();
  const companies = useAuth((state) => state.user?.companies ?? NO_COMPANIES);
  const [readState, setReadState] = useState<ReadState>('unread');
  const [severity, setSeverity] = useState<NotificationSeverity | ''>('');
  const [companyId, setCompanyId] = useState('');

  const read = readFilter(readState);
  const filters: NotificationFilters = {};

  if (read !== undefined) filters.read = read;
  if (severity !== '') filters.severity = severity;
  if (companyId !== '') filters.company_id = companyId;

  const list = useQuery({
    // `read` goes in the key on its own: the optimistic update below reads it
    // back to decide whether a row just marked read leaves the page or stays.
    queryKey: ['notifications', 'list', { read, severity, companyId }],
    queryFn: () => api.list(filters),
  });

  const refetchAll = useCallback(
    () => void client.invalidateQueries({ queryKey: ['notifications'] }),
    [client],
  );

  useNotificationsStream(
    companies.map((company) => company.id),
    refetchAll,
  );

  const markRead = useMutation({
    mutationFn: (id: string) => api.markRead(id),
    // Optimistic per `13 §6`: mark the row read in every cached page and drop
    // the unread count by one. In a page that only lists unread rows the row
    // leaves instead of dimming — that is the whole point of the default view.
    // The count is decremented even in pages that do not hold the row (the
    // bell's `limit: 1` page): the count covers rows the page does not. On
    // error everything is put back exactly as captured.
    onMutate: async (id: string) => {
      await client.cancelQueries({ queryKey: ['notifications'] });

      const previous = client.getQueriesData<NotificationList>({ queryKey: ['notifications'] });

      for (const [key, page] of previous) {
        if (page === undefined) continue;

        const unreadOnly = (key[2] as { read?: boolean } | undefined)?.read === false;
        const readAt = new Date().toISOString();

        client.setQueryData<NotificationList>(key, {
          ...page,
          data: unreadOnly
            ? page.data.filter((n) => n.id !== id)
            : page.data.map((n) => (n.id === id ? { ...n, read_at: n.read_at ?? readAt } : n)),
          meta: { ...page.meta, unread_count: Math.max(0, page.meta.unread_count - 1) },
        });
      }

      return { previous };
    },
    onError: (_error, _id, context) => {
      for (const [key, data] of context?.previous ?? []) client.setQueryData(key, data);
    },
    onSettled: refetchAll,
  });

  const markAll = useMutation({ mutationFn: () => api.markAllRead(), onSettled: refetchAll });

  const open = (notification: Notification) => {
    if (notification.read_at === null) markRead.mutate(notification.id);

    if (notification.action_url !== null) {
      // The URL is a route of this app written by the backend; the router's
      // literal-union typing cannot know that, hence the cast.
      void navigate({ to: notification.action_url as '/' });
    }
  };

  const rows = list.data?.data ?? [];
  const unreadCount = list.data?.meta.unread_count ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('notifications.title')}
        description={unreadCount > 0 ? t('notifications.unread', { count: unreadCount }) : undefined}
        actions={
          <Button
            variant="secondary"
            busy={markAll.isPending}
            disabled={unreadCount === 0}
            onClick={() => markAll.mutate()}
          >
            {t('notifications.markAllRead')}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2 text-ink-soft">
          {t('notifications.state')}
          <select
            value={readState}
            onChange={(event) => setReadState(event.target.value as ReadState)}
            className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
          >
            {READ_STATES.map((value) => (
              <option key={value} value={value}>
                {t(`notifications.state_${value}` as Key)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-ink-soft">
          {t('notifications.severity')}
          <select
            value={severity}
            onChange={(event) => setSeverity(event.target.value as NotificationSeverity | '')}
            className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
          >
            <option value="">{t('common.all')}</option>
            {SEVERITIES.map((value) => (
              <option key={value} value={value}>
                {t(`severity.${value}` as Key)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-ink-soft">
          {t('notifications.company')}
          <select
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
            className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
          >
            <option value="">{t('common.all')}</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {markAll.error !== null && <ErrorBanner error={markAll.error} />}
      {list.isPending && <SkeletonRows rows={4} />}
      {list.isError && <ErrorBanner error={list.error} onRetry={() => void list.refetch()} />}

      {list.isSuccess && rows.length === 0 && (
        <EmptyState
          icon={<Info />}
          title={readState === 'unread' ? t('notifications.nothingUnread') : t('notifications.nothingYet')}
          description={readState === 'unread' ? t('notifications.nothingUnreadHint') : t('notifications.emptyHint')}
        />
      )}

      <m.ul variants={STAGGER} initial="hidden" animate="shown" className="space-y-2">
        {rows.map((notification) => {
          const unread = notification.read_at === null;

          return (
            <m.li key={notification.id} variants={RISE} layout>
              <button
                type="button"
                onClick={() => open(notification)}
                className={cn(
                  'w-full rounded-lg border p-4 text-left transition-[border-color,background-color,box-shadow] duration-fast hover:border-primary/45 hover:shadow-sm',
                  unread ? 'border-primary/25 bg-raised' : 'border-line bg-paper',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {unread ? <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden="true" /> : null}
                  <span className={cn('text-sm text-ink', unread ? 'font-semibold' : 'font-medium')}>
                    {notification.title}
                  </span>
                  {unread ? <span className="sr-only">{t('notifications.unreadMark')}</span> : null}
                  <SeverityBadge severity={notification.severity} />
                  {notification.company !== null ? (
                    <Badge tone="primary" size="sm">
                      {notification.company.name}
                    </Badge>
                  ) : null}
                  <span className="ml-auto shrink-0 text-xs text-ink-faint">
                    {format.relative(notification.created_at)}
                  </span>
                </div>
                {notification.body !== null && notification.body !== '' ? (
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{notification.body}</p>
                ) : null}
              </button>
            </m.li>
          );
        })}
      </m.ul>
    </div>
  );
}
